import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { CONTENT_HISTORY } from '@confluence/named-routes/entry-points/routes.ts';

import { i18n } from '../changeHistoryMacroI18n.ts';
import type { ChangeHistoryVersion } from '../changeHistoryMacroTypes.ts';

import { VersionAuthor } from './VersionAuthor.tsx';
import { VersionComment } from './VersionComment.tsx';
import { VersionDate } from './VersionDate.tsx';
import { VersionLink } from './VersionLink.tsx';
import { ChangeHistoryErrorDisplay } from './ChangeHistoryErrorDisplay.tsx';

const styles = cssMap({
	tableWrapper: {
		width: '100%',
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse',
	},
	caption: {
		textAlign: 'left',
	},
	headerCell: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
		textAlign: 'left',
		verticalAlign: 'top',
	},
	cell: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		verticalAlign: 'top',
	},
	separatedCell: {
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border'),
	},
	nowrapCell: {
		whiteSpace: 'nowrap',
	},
	authorLine: {
		marginBlockEnd: token('space.025'),
	},
	moreVersionsNote: {
		marginBlockStart: token('space.050'),
	},
});

export type ChangeHistoryTableProps = {
	versions: ChangeHistoryVersion[];
	spaceKey: string;
	contentId: string;
	hasNextPage: boolean;
	onViewMore: () => void;
	isLoadingNextPage: boolean;
	loadNextPageError: Error | null;
};

export const ChangeHistoryTable = ({
	versions,
	spaceKey,
	contentId,
	hasNextPage,
	onViewMore,
	isLoadingNextPage,
	loadNextPageError,
}: ChangeHistoryTableProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.050">
			<Box xcss={styles.tableWrapper} testId="change-history-table">
				<Box as="table" xcss={styles.table}>
					<Box as="caption" xcss={styles.caption}>
						<FormattedMessage {...i18n.caption} />
					</Box>
					<Box as="thead">
						<Box as="tr">
							<Box as="th" scope="col" xcss={styles.headerCell}>
								<FormattedMessage {...i18n.pageVersionColumn} />
							</Box>
							<Box as="th" scope="col" xcss={styles.headerCell}>
								<FormattedMessage {...i18n.dateColumn} />
							</Box>
							<Box as="th" scope="col" xcss={styles.headerCell}>
								<FormattedMessage {...i18n.commentColumn} />
							</Box>
						</Box>
					</Box>
					<Box as="tbody">
						{versions.map((version, index) => {
							const isCurrent = index === 0;
							const href = CONTENT_HISTORY.toUrl(
								{
									spaceKey,
									contentId,
								},
								{ query: { version1: version.number } },
							);

							return (
								<Box as="tr" key={version.number} testId={`change-history-row-${version.number}`}>
									<Box as="td" xcss={cx(styles.cell, !isCurrent && styles.separatedCell)}>
										<Box xcss={styles.nowrapCell}>
											<VersionLink version={version} href={href} isCurrent={isCurrent} />
										</Box>
									</Box>
									<Box as="td" xcss={cx(styles.cell, !isCurrent && styles.separatedCell)}>
										<Box xcss={styles.nowrapCell}>
											<VersionDate version={version} isCurrent={isCurrent} />
										</Box>
									</Box>
									<Box as="td" xcss={cx(styles.cell, !isCurrent && styles.separatedCell)}>
										<Stack space="space.050">
											<Box xcss={styles.authorLine}>
												<VersionAuthor author={version.by} isCurrent={isCurrent} />
											</Box>
											<VersionComment version={version} isCurrent={isCurrent} />
										</Stack>
									</Box>
								</Box>
							);
						})}
					</Box>
				</Box>
			</Box>
			{hasNextPage && (
				<Box xcss={styles.moreVersionsNote}>
					<Stack space="space.050">
						<Text color="color.text.subtle" size="small">
							<FormattedMessage
								{...i18n.moreVersionsAvailable}
								values={{ count: versions.length }}
							/>
						</Text>
						<Box>
							<Button
								aria-label={formatMessage(i18n.viewMoreVersions)}
								appearance="default"
								isDisabled={isLoadingNextPage}
								isLoading={isLoadingNextPage}
								onClick={onViewMore}
								testId="change-history-view-more"
							>
								<FormattedMessage {...i18n.viewMoreVersions} />
							</Button>
						</Box>
					</Stack>
				</Box>
			)}
			{loadNextPageError && (
				<ChangeHistoryErrorDisplay error={loadNextPageError}>
					<FormattedMessage {...i18n.loadMoreError} />
				</ChangeHistoryErrorDisplay>
			)}
		</Stack>
	);
};
